import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { styled } from '@mui/material/styles';
import useReportsService from "../../services/useReportsService";
import useToast, { ToastPosition, ToastType } from "../../hooks/useToast";
import { TopFiftyClient } from "../../types/Reports";
import LicensePlate from "../LicensePlate";
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewNumberPlateDetailsDialog from "../TableView/ViewNumberPlateDetailsDialog";
import { formatMedianNumber } from "../../utils/formatNumber";

interface TopFiftyClientsReportDialogProps {
    open: boolean;
    onClose: () => void;
}

const DefaultFilters = {
    startDate: new Date().setMonth(new Date().getMonth() - 3),
    endDate: Date.now(),
}

const Divider = styled('div')({
    width: '12px',
    height: '1px',
    backgroundColor: 'white',
    margin: '0 8px',
});

function TopFiftyClientsReportDialog({ open, onClose }: TopFiftyClientsReportDialogProps) {
    const [startDate, setStartDate] = useState<number>(DefaultFilters.startDate);
    const [endDate, setEndDate] = useState<number>(DefaultFilters.endDate);
    const [selectedLicensePlate, setSelectedLicensePlate] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<TopFiftyClient[]>([]);

    const { fetchTopFiftyClientsReportData } = useReportsService();
    const { showToast } = useToast();
    const { t } = useTranslation("common");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    useEffect(() => {
        setLoading(true);
        fetchTopFiftyClientsReportData(startDate, endDate).then((reportData) => {
            setData(reportData);
        }).catch(() => {
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
                position: ToastPosition.TOP_CENTER,
            });
        }).finally(() => {
            setLoading(false);
        });
    }, [open, startDate, endDate, fetchTopFiftyClientsReportData, showToast, t]);

    const datePickers = useMemo(() => (
        <Box alignItems="center" display="flex" mb={2}>
            <DatePicker
                label={t("search.filters.from")}
                value={dayjs(startDate)}
                onChange={date => setStartDate(date ? date.valueOf() : 0)}
                slotProps={{
                    textField: {
                        size: "small",
                    },
                }}
            />
            <Divider />
            <DatePicker
                label={t("search.filters.to")}
                value={dayjs(endDate)}
                onChange={date => setEndDate(date ? date.valueOf() : 0)}
                slotProps={{
                    textField: {
                        size: "small",
                    },
                }}
            />
        </Box>
    ), [t, startDate, endDate]);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullScreen>
                <DialogTitle>
                    {t("reports.top-fifty-clients.title")}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.primary[100],
                            position: "absolute",
                            top: 16,
                            right: 16,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography>
                            {t("reports.top-fifty-clients.subtitle")}
                        </Typography>
                    </Box>
                    {datePickers}
                    {loading ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Paper
                            variant='outlined'
                            sx={{
                                flex: 1,
                                maxHeight: '100%',
                                overflowX: 'hidden',
                                width: '100%',
                            }}
                        >
                            <TableContainer sx={{ maxHeight: '100%' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">{t(`reports.top-fifty-clients.position${isMobile ? '.short' : ''}`)}</TableCell>
                                            <TableCell align="center">{t(`reports.top-fifty-clients.numberPlate${isMobile ? '.short' : ''}`)}</TableCell>
                                            <TableCell align="center">{t(`reports.top-fifty-clients.numberOfEntries${isMobile ? '.short' : ''}`)}</TableCell>
                                            <TableCell align="center">{t(`reports.top-fifty-clients.firstVisit${isMobile ? '.short' : ''}`)}</TableCell>
                                            <TableCell align="center">{t(`reports.top-fifty-clients.averageMonthlyFrequency${isMobile ? '.short' : ''}`)}</TableCell>
                                            <TableCell align="center"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {
                                            data.map((client, index) => (
                                                <TableRow
                                                    key={client.licensePlate}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                                                        },
                                                    }}
                                                >
                                                    <TableCell align="center">{index + 1}</TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <LicensePlate licenseNumber={client.licensePlate} />
                                                    </TableCell>
                                                    <TableCell align="center">{client.entryCount}</TableCell>
                                                    <TableCell align="center">{dayjs(client.firstVisit).format('DD.MM.YYYY')}</TableCell>
                                                    <TableCell align="center">{formatMedianNumber(client.monthlyAvgEntries)}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton onClick={() => {
                                                            setSelectedLicensePlate(client.licensePlate);
                                                        }}>
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </DialogContent>
            </Dialog>
            {!!selectedLicensePlate && (
                <ViewNumberPlateDetailsDialog
                    licensePlate={selectedLicensePlate}
                    open={!!selectedLicensePlate}
                    onClose={() => {
                        setSelectedLicensePlate(null);
                    }}
                />
            )}
        </>
    );
}

export default TopFiftyClientsReportDialog;
